import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { MetricFilterType, SourceCategory } from 'webapp/enums/metric-filters';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isVerified } from 'webapp/utils/verification';

import type { Metric } from 'webapp/models-custom';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';

const MetricSearchRelevance = {
	name: 2,
	'source.title': 1,
};

export default class FilterSortMetricInDesigner extends FilterSort<Metric> {
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	// custom sorting defined in left-panel/metrics.ts
	@tracked override sortProp = '';

	override searchProps = MetricSearchRelevance;

	override typedFilterEventName = TrackingEvent.DATABLOCK_SEARCH; // logging

	constructor(
		options: FilterSortSettings<Metric>,
		extraOptions: { preselectedSource?: string } = {},
	) {
		super(options);

		this.createFilter<MetricFilterType>({
			id: 1,
			label: 'Show',
			name: 'Metric type',
			selected: MetricFilterType.ALL,
			isMultiSelect: true,
			getOptions: () => {
				return this.typeOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((filterType) => {
					switch (filterType) {
						case MetricFilterType.FAVORITE:
							return item.isFavorite;
						case MetricFilterType.BASIC:
							return !item.isTypeBuilt && !item.isTypeMetricCalculationOrGeneric;
						case MetricFilterType.QUERY:
							return item.isTypeBuilt;
						case MetricFilterType.SCRIPT:
							return item.isTypeMetricCalculationOrGeneric;
						case MetricFilterType.VERIFIED:
							return isVerified(item.verificationInfo);
						default:
							return true;
					}
				});
			},
		});

		this.createFilter<SourceCategory | string>({
			id: 2,
			label: 'Data source',
			name: 'data source',
			selected: extraOptions.preselectedSource || SourceCategory.ALL,
			extraClass: 'metric-select-sources',
			searchableProperties: ['text', 'text2'],
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.sourceOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((sourceCategory) => {
					const isConnected = !!item.spaceAccessId;

					switch (sourceCategory) {
						case SourceCategory.ALL:
							return true;
						case SourceCategory.CONNECTED_SOURCES:
							return isConnected;
						case SourceCategory.INTEGRATIONS:
							return !isConnected;
						default:
							if (sourceCategory === 'Excel') {
								return !!item.sourceType?.isExcel;
							} else {
								return item.isFromSourceListingValue(sourceCategory);
							}
					}
				});
			},
		});
	}

	@cached
	get sourceOptions(): FilterOption<SourceCategory | string>[] {
		const datesetIconName: IconName = 'Connector',
			datasetAffixIconName: IconName = 'Table';

		const connectedSources = this.mmSourcesService
			.sortSourcesWithDatasets(
				this.mmSourcesService.sourceModels,
				'sourceTypePopularity',
				'title',
			)
			.map((source) => {
				return {
					id: source.id!,
					text: source.title,
					text2: source.sourceType.name,
					img: source.sourceType.avatar,
					typeKey: source.sourceType.key,
					statusObject: source.statusObject,
					isDataset: source.isDataset,
					isDisabled: source.isDatasetAndDisabledForFilterSelection,
					text3: source.infoForFilter,
					icon: source.isDataset ? datesetIconName : undefined,
					affixIcon: source.isDataset ? datasetAffixIconName : undefined,
				};
			});

		const genericSources: FilterOption<SourceCategory | string>[] = [];
		this.mmSourceTypesService.connectableSourceTypeModels
			.filter((item) => !item.isHidden && !item.showInGroupOnly)
			.forEach((sourceType) => {
				const alreadyInConnected = !!connectedSources.find(
					(s) => s.typeKey === sourceType.key,
				);
				if (!alreadyInConnected) {
					genericSources.push({
						id: sourceType.key,
						text: sourceType.name,
						img: sourceType.avatar,
					});
				}
			});

		const list: FilterOption<SourceCategory | string>[] = [
			{ id: SourceCategory.ALL, text: 'All' },
			{
				id: SourceCategory.CONNECTED_SOURCES,
				text: 'Data sources',
				class: 'seperator',
			},
			...connectedSources,
			{ id: SourceCategory.INTEGRATIONS, text: 'Available integrations', class: 'seperator' },
			...genericSources,
		];

		return list;
	}

	@cached
	get typeOptions(): FilterOption<MetricFilterType>[] {
		return [
			{ id: MetricFilterType.ALL, text: 'All' },
			...(this.mmMetricsService.showFavoriteAction
				? [{ id: MetricFilterType.FAVORITE, text: 'Favorite' }]
				: []),
			{ id: MetricFilterType.VERIFIED, text: 'Verified' },
			{ id: MetricFilterType.BASIC, text: 'Prebuilt' },
			{ id: MetricFilterType.QUERY, text: 'Built' },
			{ id: MetricFilterType.SCRIPT, text: 'Calculated' },
		];
	}
}
